import React, { useEffect, useState } from 'react';
import { manuscriptsAPI, reviewsAPI, reviewersAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Clock, CheckCircle, XCircle, AlertCircle, Download, UserPlus, FileSearch, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
  'En Revision': { color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock },
  'Revisiones Requeridas': { color: 'bg-orange-50 text-orange-700 border-orange-200', icon: AlertCircle },
  'Aceptado': { color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle },
  'Rechazado': { color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  'Publicado': { color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle },
};

export default function ReviewerDashboard() {
  const [manuscripts, setManuscripts] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewForm, setReviewForm] = useState({ manuscriptId: '', decision: '', comentarios: '', comentarios_privados: '' });
  const [assignForm, setAssignForm] = useState({ manuscriptId: '', reviewerId: '' });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const { user, isAdmin, isEditor } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const isEditorial = isAdmin() || isEditor();

  const handleDownload = async (manuscriptId) => {
    setDownloading(manuscriptId);
    try {
      await manuscriptsAPI.downloadBlob(manuscriptId);
      toast.success('Descarga iniciada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo descargar el manuscrito. Intenta de nuevo.');
    } finally {
      setDownloading(null);
    }
  };

  const loadData = () => {
    manuscriptsAPI.list().then(res => setManuscripts(res.data.items || res.data)).catch(() => {}).finally(() => setLoading(false));
    if (isAdmin() || isEditor()) {
      reviewersAPI.list().then(res => setReviewers(res.data)).catch(() => {});
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleAssign = async () => {
    try {
      await manuscriptsAPI.assignReviewer(assignForm.manuscriptId, assignForm.reviewerId);
      setAssignDialogOpen(false);
      toast.success('Revisore asignade exitosamente');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleReview = async () => {
    if (!reviewForm.decision || !reviewForm.comentarios.trim()) return;
    try {
      const res = await reviewsAPI.create({
        manuscript_id: reviewForm.manuscriptId,
        decision: reviewForm.decision,
        comentarios: reviewForm.comentarios,
        comentarios_privados: reviewForm.comentarios_privados
      });
      setReviewDialogOpen(false);
      setReviewForm({ manuscriptId: '', decision: '', comentarios: '', comentarios_privados: '' });
      const newStatus = res.data?.new_status;
      toast.success(isEditorial ? `Decision editorial aplicada: ${newStatus}` : 'Dictamen enviado exitosamente');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar dictamen');
    }
  };

  const myManuscripts = manuscripts.filter(m => {
    if (isAdmin() || isEditor()) return true;
    // Check both old single-reviewer and new multi-reviewer format
    if (m.reviewers && m.reviewers.some(r => r.id === user?.id)) return true;
    return m.reviewer_id === user?.id;
  });

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="reviewer-dashboard">
      <div className="max-w-5xl">
        <h1 className="text-2xl font-serif font-bold text-slate-dark mb-2">Panel de Revision</h1>
        <p className="text-sm font-sans text-gray-400 mb-8">Revisa los manuscritos asignados y envia tus dictamenes.</p>

        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-trans-blue border-t-transparent rounded-full" /></div>
        ) : myManuscripts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-sm border border-gray-100">
            <FileSearch className="w-12 h-12 text-gray-300 mx-auto mb-4" strokeWidth={1} />
            <p className="font-sans text-gray-400">No hay manuscritos asignados.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {myManuscripts.map((m, i) => {
              const sc = statusConfig[m.status] || statusConfig['En Revision'];
              const Icon = sc.icon;
              return (
                <div key={m.id} className="p-5 bg-white border border-gray-100 rounded-sm" data-testid={`reviewer-manuscript-${i}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className={`text-xs font-sans ${sc.color} flex items-center gap-1`}>
                          <Icon className="w-3 h-3" /> {m.status}
                        </Badge>
                        <span className="text-xs font-sans text-gray-400">{m.categoria}</span>
                      </div>
                      <h3 className="font-serif font-semibold text-slate-dark leading-snug">{m.titulo}</h3>
                      <p className="text-sm font-sans text-gray-400 mt-1">Por: {m.autor_name} &middot; {new Date(m.created_at).toLocaleDateString('es-MX')}</p>
                      <p className="text-sm font-serif text-gray-500 mt-2 line-clamp-2">{m.abstract_es}</p>
                      {/* Deadline */}
                      {m.review_deadline && (
                        <div className="mt-2 flex items-center gap-1.5 text-xs font-sans">
                          <Clock className="w-3 h-3 text-gray-400" />
                          {(() => {
                            const dl = new Date(m.review_deadline);
                            const days = Math.ceil((dl - new Date()) / 86400000);
                            const ds = dl.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
                            if (days < 0) return <span className="text-red-500 font-medium">Plazo vencido ({ds})</span>;
                            if (days <= 3) return <span className="text-amber-600 font-medium">Vence en {days} dia{days !== 1 ? 's' : ''} ({ds})</span>;
                            return <span className="text-gray-500">Plazo: {ds}</span>;
                          })()}
                        </div>
                      )}
                      {(m.reviewers?.length > 0 || m.reviewer_name) && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {(m.reviewers || []).map(r => (
                            <span key={r.id} className="text-[10px] font-sans text-trans-blue bg-trans-blue/5 border border-trans-blue/20 rounded-md px-2 py-0.5">{r.name}</span>
                          ))}
                          {!m.reviewers?.length && m.reviewer_name && (
                            <span className="text-xs font-sans text-trans-blue">{m.reviewer_name}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3 sm:mt-0 sm:flex-col sm:shrink-0">
                      {m.file_available && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="font-sans text-xs"
                          disabled={downloading === m.id}
                          onClick={() => handleDownload(m.id)}
                          data-testid={`download-manuscript-${i}`}
                        >
                          {downloading === m.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
                          Descargar
                        </Button>
                      )}
                      {(isAdmin() || isEditor()) && !['Publicado', 'Rechazado'].includes(m.status) && (
                        <Button variant="outline" size="sm" className="font-sans text-xs" onClick={() => { setAssignForm({ manuscriptId: m.id, reviewerId: '' }); setAssignDialogOpen(true); }} data-testid={`assign-reviewer-${i}`}>
                          <UserPlus className="w-3 h-3 mr-1" /> Asignar
                        </Button>
                      )}
                      {m.status !== 'Publicado' && m.status !== 'Rechazado' && (isEditorial || !m.my_review) && (
                        <Button size="sm" className="font-sans text-xs bg-slate-dark" onClick={() => { setReviewForm({ manuscriptId: m.id, decision: '', comentarios: '', comentarios_privados: '' }); setReviewDialogOpen(true); }} data-testid={`review-manuscript-${i}`}>
                          <Send className="w-3 h-3 mr-1" /> {isEditorial ? 'Decision editorial' : 'Dictamen'}
                        </Button>
                      )}
                      {!isEditorial && m.my_review && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-sans text-green-700 bg-green-50 border border-green-200 rounded-md px-2 py-1" data-testid={`review-submitted-${i}`}>
                          <CheckCircle className="w-3 h-3" /> Dictamen enviado: {m.my_review}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assign Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-serif">Asignar Revisore</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Select onValueChange={v => setAssignForm(f => ({ ...f, reviewerId: v }))} data-testid="assign-reviewer-select">
              <SelectTrigger className="font-sans"><SelectValue placeholder="Seleccionar revisore..." /></SelectTrigger>
              <SelectContent>
                {reviewers.map(r => (
                  <SelectItem key={r.id} value={r.id}>{r.name} {r.apellidos} ({r.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={handleAssign} className="w-full bg-slate-dark font-sans" disabled={!assignForm.reviewerId} data-testid="confirm-assign-btn">Asignar</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-serif">{isEditorial ? 'Decision Editorial' : 'Enviar Dictamen'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {isEditorial && (
              <p className="text-xs font-sans text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2.5" data-testid="editorial-note">
                Como parte del equipo editorial tu decision es final y se aplica de inmediato, sin esperar el consenso de revisores.
              </p>
            )}
            <div>
              <label className="text-sm font-sans font-medium text-gray-700">Decision *</label>
              <Select value={reviewForm.decision} onValueChange={v => setReviewForm(f => ({ ...f, decision: v }))}>
                <SelectTrigger className="font-sans mt-1" data-testid="review-decision-select"><SelectValue placeholder="Seleccionar decision..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Aceptar" data-testid="decision-aceptar">Aceptar</SelectItem>
                  <SelectItem value="Revisiones Requeridas" data-testid="decision-revisiones">Revisiones Requeridas</SelectItem>
                  <SelectItem value="Rechazar" data-testid="decision-rechazar">Rechazar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-sans font-medium text-gray-700">Comentarios para le autore *</label>
              <Textarea value={reviewForm.comentarios} onChange={e => setReviewForm(f => ({ ...f, comentarios: e.target.value }))} placeholder="Comentarios visibles para le autore..." rows={4} className="mt-1 font-sans" data-testid="review-comments" />
            </div>
            <div>
              <label className="text-sm font-sans font-medium text-gray-700">Notas privadas (solo editores)</label>
              <Textarea value={reviewForm.comentarios_privados} onChange={e => setReviewForm(f => ({ ...f, comentarios_privados: e.target.value }))} placeholder="Notas privadas..." rows={2} className="mt-1 font-sans" data-testid="review-private-notes" />
            </div>
            <Button onClick={handleReview} className="w-full bg-slate-dark font-sans" disabled={!reviewForm.decision || !reviewForm.comentarios} data-testid="submit-review-btn">Enviar Dictamen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
